import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, t } from "../lib/api";
import { useAsync } from "../lib/hooks";
import { useSession } from "../lib/session";
import { Empty, ErrorNote, Modal } from "../components/ui";

export interface EngagementSummary {
  id: string;
  name: string;
  clientUnit: string | null;
  country: string;
  phase: string;
  interviewerLanguages: string[];
  intervieweeLanguages: string[];
  focusModules: string[];
  targetInterviews: number | null;
  publishedToClient: boolean;
  counts: Record<string, number>;
}

const PHASES: Record<string, string> = { mobilise: "Mobilise", discover: "Discover", diagnose: "Diagnose", design: "Design", deliver: "Deliver and sustain" };

export function EngagementsPage() {
  const { tenant } = useParams();
  const { me } = useSession();
  const list = useAsync(() => api.get<EngagementSummary[]>(`${t(tenant!)}/engagements`), [tenant]);
  const [creating, setCreating] = useState(false);
  const role = me?.workspaces.find((w) => w.slug === tenant)?.role;
  const canCreate = role === "admin" || role === "lead";

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Engagements</h1>
          <p className="sub">{me?.workspaces.find((w) => w.slug === tenant)?.name}</p>
        </div>
        <span className="spacer" />
        {canCreate && (
          <button className="btn primary" onClick={() => setCreating(true)}>
            New engagement
          </button>
        )}
      </div>
      <ErrorNote error={list.error} />
      {list.data && list.data.length === 0 && (
        <div className="panel">
          <Empty title="No engagements yet">{canCreate ? "Create the first engagement to plan interviews and build the question guide." : "You'll see engagements here once a lead adds you to one."}</Empty>
        </div>
      )}
      {list.data && list.data.length > 0 && (
        <div className="panel flush">
          <table className="table">
            <thead>
              <tr>
                <th>Engagement</th>
                <th>Phase</th>
                <th>Languages</th>
                <th>Interviews done</th>
                <th>Verified</th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((e) => {
                const done = ["processing", "draft", "reviewed", "verified"].reduce((a, k) => a + (e.counts[k] ?? 0), 0);
                return (
                  <tr key={e.id}>
                    <td>
                      <Link to={`/w/${tenant}/e/${e.id}`} style={{ fontWeight: 600, textDecoration: "none" }}>
                        {e.name}
                      </Link>
                      <div className="muted xs">
                        {e.clientUnit ?? "—"} · {e.country}
                        {e.focusModules.length > 0 && " · focus review on"}
                      </div>
                    </td>
                    <td>{PHASES[e.phase] ?? e.phase}</td>
                    <td className="small">
                      {e.interviewerLanguages.join(", ")} ↔ {e.intervieweeLanguages.join(", ")}
                    </td>
                    <td>
                      {done}
                      {e.targetInterviews ? <span className="muted"> of {e.targetInterviews}</span> : null}
                    </td>
                    <td>{e.counts.verified ?? 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {creating && <CreateEngagement onClose={() => setCreating(false)} />}
    </>
  );
}

function LangPicker({ value, onChange, label }: { value: string[]; onChange: (v: string[]) => void; label: string }) {
  const { config } = useSession();
  return (
    <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
      <span>{label}</span>
      <div className="row wrap" style={{ gap: 6 }}>
        {config.languages.map((l) => {
          const on = value.includes(l.code);
          return (
            <button
              type="button"
              key={l.code}
              className={`btn small ${on ? "primary" : ""}`}
              aria-pressed={on}
              onClick={() => onChange(on ? value.filter((v) => v !== l.code) : [...value, l.code])}
              title={l.support === "excellent" ? "Excellent support" : `${l.support} support${l.sttStatus === "preview" ? " — speech recognition in preview" : ""}`}
            >
              {l.name}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

function CreateEngagement({ onClose }: { onClose: () => void }) {
  const { tenant } = useParams();
  const { config } = useSession();
  const nav = useNavigate();
  const [f, setF] = useState({
    name: "",
    clientUnit: "",
    country: "MX",
    phase: "discover",
    interviewerLanguages: ["en-US"],
    intervieweeLanguages: ["es-MX"],
    reportLanguage: "en-US",
    focusModules: [] as string[],
    targetInterviews: "",
  });
  const [error, setError] = useState<Error | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ id: string }>(`${t(tenant!)}/engagements`, {
        ...f,
        clientUnit: f.clientUnit || undefined,
        targetInterviews: f.targetInterviews ? Number(f.targetInterviews) : undefined,
      });
      nav(`/w/${tenant}/e/${r.id}/plan`);
    } catch (err) {
      setError(err as Error);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="New engagement" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <label className="field">
          <span>Engagement name</span>
          <input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required minLength={2} placeholder="Collections and field operations review" />
        </label>
        <div className="grid2">
          <label className="field">
            <span>Client or business unit</span>
            <input className="input" value={f.clientUnit} onChange={(e) => setF({ ...f, clientUnit: e.target.value })} />
          </label>
          <label className="field">
            <span>Country (ISO code)</span>
            <input className="input" value={f.country} maxLength={2} onChange={(e) => setF({ ...f, country: e.target.value.toUpperCase() })} required />
          </label>
        </div>
        <LangPicker label="Interviewers speak" value={f.interviewerLanguages} onChange={(v) => setF({ ...f, interviewerLanguages: v })} />
        <LangPicker label="Interviewees speak" value={f.intervieweeLanguages} onChange={(v) => setF({ ...f, intervieweeLanguages: v })} />
        <div className="grid2">
          <label className="field">
            <span>Report language</span>
            <select className="input" value={f.reportLanguage} onChange={(e) => setF({ ...f, reportLanguage: e.target.value })}>
              {config.languages.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Interviews planned</span>
            <input className="input" type="number" min={0} value={f.targetInterviews} onChange={(e) => setF({ ...f, targetInterviews: e.target.value })} />
          </label>
        </div>
        {config.focusModules.length > 0 && (
          <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
            <span>Focus reviews</span>
            {config.focusModules.map((m) => (
              <label key={m.key} className="check" style={{ alignItems: "flex-start" }}>
                <input
                  type="checkbox"
                  checked={f.focusModules.includes(m.key)}
                  onChange={(e) => setF({ ...f, focusModules: e.target.checked ? [...f.focusModules, m.key] : f.focusModules.filter((k) => k !== m.key) })}
                />
                <span>
                  <b>{m.name}</b>
                  <br />
                  <span className="muted small">{m.summary}</span>
                </span>
              </label>
            ))}
          </fieldset>
        )}
        <ErrorNote error={error} />
        <div className="row">
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={busy}>
            Create engagement
          </button>
        </div>
      </form>
    </Modal>
  );
}
